/**
 * Staff-facing attachments manager.
 *
 * Used in two places:
 *   1. ShareProposal mint dialog - to upload files at link-creation
 *      time (the "force attachments" UX when the toggle is ON).
 *   2. EngagementPanel - to add / remove attachments on existing
 *      links without re-minting.
 *
 * Hits the staff endpoints under /api/proposals/share/{token}/attachments.
 * Validates extension + size client-side for instant feedback; the
 * backend re-validates so a tampered request still fails server-side.
 *
 * Renders one of two states depending on whether `token` is provided:
 *   - WITH token: live mode, talks to the server, lists current files.
 *   - WITHOUT token: queue mode - collects File objects in a local
 *     array (uncontrolled) so the caller can upload after minting.
 *     The caller passes `onQueueChange` to know what's queued.
 *
 * Returns the same chips/dropzone visuals either way for consistency.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Paperclip, Upload, X, Loader2, AlertCircle,
  FileSpreadsheet, FileText, FileImage, File as FileIcon,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ALLOWED_EXTS = new Set([
  "xlsx", "xls", "docx", "doc", "pdf", "csv", "txt",
]);
const MAX_BYTES = 25 * 1024 * 1024;  // 25 MB - must match share_attachments.py
const MAX_FILES = 10;

const fmtSize = (b) => {
  if (!b) return "-";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const extOf = (name) => (name || "").split(".").pop()?.toLowerCase() || "";

const iconFor = (ext) => {
  switch (ext) {
    case "xlsx": case "xls": case "csv": return FileSpreadsheet;
    case "docx": case "doc": case "pdf": case "txt": return FileText;
    case "png": case "jpg": case "jpeg": return FileImage;
    default: return FileIcon;
  }
};


export default function AttachmentsSection({
  token,                // when set, live mode (server-backed)
  queue,                // when set, queue mode (caller-controlled)
  onQueueChange,        // called with the new queue array
  compact = false,      // tighter visuals for EngagementPanel embed
  testIdPrefix = "attachments",
}) {
  const liveMode = !!token;
  const [items, setItems] = useState(null);  // server list (live mode)
  const [busy, setBusy] = useState(false);   // global busy (during a single upload)
  const fileInputRef = useRef(null);

  // Pull current list when in live mode.
  useEffect(() => {
    if (!liveMode) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/api/proposals/share/${encodeURIComponent(token)}/attachments`)
      .then((r) => { if (!cancelled) setItems(r.data?.attachments || []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [token, liveMode]);

  const visibleItems = liveMode ? (items || []) : (queue || []);
  const count = visibleItems.length;

  const validateLocally = (file) => {
    const ext = extOf(file.name);
    if (!ALLOWED_EXTS.has(ext)) {
      toast.error(`"${file.name}" - only xlsx, xls, docx, doc, pdf, csv, txt allowed.`);
      return false;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`"${file.name}" exceeds 25 MB.`);
      return false;
    }
    if (count >= MAX_FILES) {
      toast.error(`Max ${MAX_FILES} attachments per link.`);
      return false;
    }
    return true;
  };

  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    for (const file of arr) {
      if (!validateLocally(file)) continue;
      if (liveMode) {
        await uploadOne(file);
      } else {
        const next = [...(queue || []), file];
        onQueueChange?.(next);
      }
    }
  };

  const uploadOne = async (file) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(
        `${API}/api/proposals/share/${encodeURIComponent(token)}/attachments`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setItems((prev) => [data, ...(prev || [])]);
      toast.success(`Uploaded ${file.name}`);
    } catch (e) {
      const reason = e?.response?.data?.detail?.reason || "Upload failed";
      toast.error(`${file.name}: ${reason.replaceAll("_", " ")}`);
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (att, idx) => {
    if (liveMode) {
      try {
        await axios.delete(
          `${API}/api/proposals/share/${encodeURIComponent(token)}/attachments/${encodeURIComponent(att.id)}`,
        );
        setItems((prev) => (prev || []).filter((a) => a.id !== att.id));
        toast.success(`Removed ${att.filename}`);
      } catch {
        toast.error("Remove failed.");
      }
    } else {
      const next = (queue || []).filter((_, i) => i !== idx);
      onQueueChange?.(next);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  return (
    <div data-testid={testIdPrefix}>
      {/* Dropzone */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid={`${testIdPrefix}-dropzone`}
        className={`cursor-pointer flex items-center justify-center gap-2 rounded-lg border-2 border-dashed transition-colors ${
          compact ? "py-3 px-3 text-xs" : "py-4 px-4 text-sm"
        } ${
          count >= MAX_FILES
            ? "border-stone-200 bg-stone-50 cursor-not-allowed opacity-60"
            : "border-stone-300 bg-stone-50/50 hover:border-[#1B4F8A] hover:bg-[#1B4F8A]/5"
        }`}
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin text-[#1B4F8A]" />
        ) : (
          <Upload className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} text-[#1B4F8A]`} />
        )}
        <span className="text-stone-600">
          {count >= MAX_FILES
            ? `Max ${MAX_FILES} files reached`
            : busy
            ? "Uploading…"
            : compact
            ? "Drop a file or click to attach"
            : "Drop a file here, or click to browse"}
        </span>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          accept=".xlsx,.xls,.docx,.doc,.pdf,.csv,.txt"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`${testIdPrefix}-input`}
        />
      </div>

      <p className={`text-[10px] font-mono text-stone-400 mt-1.5 flex items-center gap-1 ${compact ? "" : ""}`}>
        <AlertCircle className="w-3 h-3" />
        xlsx · xls · docx · doc · pdf · csv · txt - up to 25 MB each, max 10 files
      </p>

      {/* Files list */}
      {count > 0 && (
        <ul
          data-testid={`${testIdPrefix}-list`}
          className={`mt-2 space-y-1 ${compact ? "" : ""}`}
        >
          {visibleItems.map((att, idx) => {
            const isQueue = !liveMode;
            const filename = att.filename || att.name;
            const size = att.size_bytes ?? att.size ?? 0;
            const ext = extOf(filename);
            const Icon = iconFor(ext);
            return (
              <li
                key={liveMode ? att.id : `${filename}-${idx}`}
                data-testid={`${testIdPrefix}-item-${idx}`}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-md bg-stone-50 border border-stone-200 ${compact ? "text-xs" : "text-sm"}`}
              >
                <Icon className="w-3.5 h-3.5 text-[#1B4F8A] shrink-0" />
                <span className="flex-1 truncate font-mono text-slate-800" title={filename}>{filename}</span>
                <span className="text-[10px] font-mono text-stone-400 shrink-0">{fmtSize(size)}</span>
                {isQueue && (
                  <span className="text-[9px] font-mono uppercase tracking-wider text-amber-700 shrink-0">
                    queued
                  </span>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeOne(att, idx); }}
                  aria-label={`Remove ${filename}`}
                  className="text-stone-400 hover:text-rose-600 transition-colors shrink-0"
                  data-testid={`${testIdPrefix}-remove-${idx}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


// Convenience export: helper to upload a queue of File objects
// against a freshly-minted token, used by ShareProposal post-mint.
export async function flushQueueToToken(token, queueFiles) {
  if (!token || !queueFiles?.length) return [];
  const results = [];
  for (const file of queueFiles) {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(
        `${API}/api/proposals/share/${encodeURIComponent(token)}/attachments`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      results.push({ ok: true, file, data });
    } catch (e) {
      const reason = e?.response?.data?.detail?.reason || "upload_failed";
      results.push({ ok: false, file, error: reason });
    }
  }
  return results;
}
